import React, { useRef, useState, forwardRef, useImperativeHandle, useCallback, useEffect } from 'react';
import OTPInput, { type OTPInputRef } from "./OTPInput";
import Loading from './Loading';
import TurnstileWidget from './TurnstileWidget';
import { waitForTurnstile, inIframe } from '../common/utils';
import { recoverAuthData } from '../common/authStorage';
interface SallaVerifyProps {
  display?: 'inline' | 'modal';
  type?: 'mobile' | 'email';
  autoReload?: boolean;
  supportWebAuth?: boolean;
  salla: any;
  verified?: (response: unknown) => void;
}
type DataType = {
  type: 'mobile' | 'email',
  phone?: string,
  country_code?: string,
  country_key?: string,
  email?: string
};
const SallaVerify = forwardRef(({ salla, verified, supportWebAuth }: SallaVerifyProps, ref) => {
  const [timer, setTimer] = useState(30);
  const [hasError, setHasError] = useState(false);
  const [dataErrorMsg] = useState(salla.lang.get('common.errors.data_error'));
  const [enterOtpError] = useState(salla.lang.get('pages.profile.verify_placeholder'));
  const [otpError] = useState(salla.lang.get('pages.profile.verify_wrong'));
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line
  const [code, setCode] = useState('');
  const [otp, setOtp] = useState('');
  const [data, setData] = useState<DataType>({ type: 'mobile' });
  const [autoRecovered, setAutoRecovered] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const otpInputRef = useRef<OTPInputRef | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const resendMessageRef = useRef<HTMLParagraphElement>(null);
  const timerRef = useRef<HTMLElement>(null);
  const resendRef = useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => ({
    open: (data: DataType) => {
      salla.logger.log('Verify open called with data:', data);
      
      if (!data || !data.type) {
        salla.logger.error('Verify Error: Invalid data structure', data);
        Salla.analytics.log('Verify Error::open', { 
          error_type: 'invalid_data', 
          data: JSON.stringify(data),
          auto_recovered: autoRecovered
        });
        
        // Try to recover from localStorage
        const storeId = salla.config.get('store.id') || 'unknown';
        const recovered = recoverAuthData(storeId, salla.logger);
        if (recovered) {
          salla.logger.log('Verify open: Recovered from localStorage');
          
          setData(recovered);
          setDataReady(true);
          resendTimer();
          resetError();
          setTimeout(() => {
            otpInputRef.current?.focusFirstInput();
          }, 100);
          return;
        }
        
        Salla.analytics.log('Verify Error::open::recovery_failed', { 
          error_type: 'invalid_data_no_recovery'
        });
        setHasError(true);
        setErrorMessage('Verification data missing. Please try logging in again.');
        return;
      }
      
      const isValid = data.type === 'email' ? !!data.email : (!!data.phone && !!data.country_code);
      if (!isValid) {
        salla.logger.error('Verify Error: Missing required fields', data);
        Salla.analytics.log('Verify Error::open', { 
          error_type: 'missing_required_fields', 
          type: data.type,
          has_email: !!data.email,
          has_phone: !!data.phone,
          has_country_code: !!data.country_code,
          auto_recovered: autoRecovered
        });
        
        // Try to recover from localStorage
        const storeId = salla.config.get('store.id') || 'unknown';
        const recovered = recoverAuthData(storeId, salla.logger);
        if (recovered) {
          salla.logger.log('Verify open: Recovered from localStorage');
          
          setData(recovered);
          setDataReady(true);
          resendTimer();
          resetError();
          setTimeout(() => {
            otpInputRef.current?.focusFirstInput();
          }, 100);
          return;
        }
        
        Salla.analytics.log('Verify Error::open::recovery_failed', { 
          error_type: 'missing_fields_no_recovery'
        });
        setHasError(true);
        setErrorMessage('Incomplete verification data. Please try logging in again.');
        return;
      }
      
      // Data is valid, proceed normally
      salla.logger.log('Verify: Data validated successfully', data);
      
      setData(data);
      setDataReady(true);
      resendTimer();
      resetError();
      setTimeout(() => {
        otpInputRef.current?.focusFirstInput();
      }, 100);

    },
    getCode: () => {
      return otp;
    }
  }));
  const resetError = () => {
    setHasError(false);
    setErrorMessage('');
  };


  const resendCode = async () => {
    resendRef.current?.setAttribute("disabled", "disabled");
    otpInputRef.current?.reset();
    
    let resendData = data;
    if (!data.email && !data.phone) {
      salla.logger.warn('Resend: Data missing, attempting recovery');
      const storeId = salla.config.get('store.id') || 'unknown';
      const recovered = recoverAuthData(storeId, salla.logger);
      
      if (recovered) {
        resendData = recovered;
        setData(resendData);
        setDataReady(true);
        salla.logger.log('Resend: Successfully recovered data from localStorage');
      } else {
        salla.logger.error('Resend Error: Unable to recover data from localStorage');
        Salla.analytics.log('Resend Error::no_data', { 
          error_type: 'no_data_available',
          auto_recovered: autoRecovered,
          store_id: storeId
        });
        resendRef.current?.removeAttribute("disabled");
        setHasError(true);
        setErrorMessage('Unable to resend code. Please try logging in again.');
        return;
      }
    }
    
    // Track OTP Resend Requested
    Salla.analytics.track('OTP Resend Requested', {
      type: resendData.type,
      method: resendData.type === 'email' ? 'email' : 'mobile'
    });
    
    const isPassed = await waitForTurnstile("#cloudflare-turnstile-resend-widget");

    if (isPassed) {
      salla.logger.log('Resend: Sending request with data', resendData);
      return salla.api.auth.resend(resendData)
        .then(() => {
          // Track OTP Resend Sent
          Salla.analytics.track('OTP Resend Request Sent', {
            type: resendData.type,
            method: resendData.type === 'email' ? 'email' : 'mobile'
          });
        })
        .catch((error: any) => {
          // Track OTP Resend Request Failed
          Salla.analytics.track('OTP Resend Request Failed', {
            type: resendData.type,
            method: resendData.type === 'email' ? 'email' : 'mobile',
            error: error.response?.data?.error?.message || error.message || 'Unknown error'
          });
        })
        .finally(() => {
          btnRef.current?.removeAttribute("disabled");
          resendRef.current?.removeAttribute("disabled");
          resendTimer();
        });
    }

    resendRef.current?.removeAttribute("disabled");
    setHasError(true);
    setErrorMessage("Can't Start Validation!");
    return;
  };
  const handleSubmitOTP = (value: string) => {
    setCode(value);
    const updatedCode = value.split('').map((item: string) => {
      return Number.parseInt(item);
    });

    setOtp(updatedCode.join(''));

  }
  const handleChange = (value: string) => {
    resetError();
    setOtp(value);
  };

  const submit = () => {
    if (otp.length !== 4) {
      setHasError(true);
      setErrorMessage(enterOtpError);
      return;
    }

    // Ensure email or phone is present
    let submitData = data;
    if (!data.email && !data.phone) {
      salla.logger.error('Submit Error: Missing email and phone', data);
      Salla.analytics.log("Login Error::onVerify", { 
        error_type: "verify::missing_data", 
        data_type: data.type,
        auto_recovered: autoRecovered,
        error_details: JSON.stringify(data)
      });
      
      // Last attempt: Try to recover from localStorage
      const storeId = salla.config.get('store.id') || 'unknown';
      const recovered = recoverAuthData(storeId, salla.logger);
      
      if (recovered && (recovered.email || recovered.phone)) {
        submitData = recovered;
        setData(submitData);
        setDataReady(true);
        salla.logger.log('Submit: Recovered data successfully from localStorage');
      } else {
        salla.logger.error('Submit Error: Data recovery failed - localStorage empty or expired');
        Salla.analytics.log("Login Error::onVerify::recovery_failed", { 
          error_type: "data_recovery_failed",
          auto_recovered: autoRecovered,
          store_id: storeId
        });
        setHasError(true);
        setErrorMessage(dataErrorMsg + ' Please try logging in again.');
        return;
      }
    }

    // Ensure correct type based on data
    const updatedData = {
      ...submitData,
      type: submitData.email ? 'email' : 'mobile',
    };

    // Exclude country_key
    const { country_key, ...restData } = updatedData;
    const dataToSend = { code: otp, ...restData };

    // Track OTP Submitted
    Salla.analytics.track('OTP Submitted', {
      type: submitData.type,
      method: submitData.type === 'email' ? 'email' : 'mobile'
    });

    salla.logger.log('Submit: Final payload', dataToSend);
    return Promise.resolve()
      .then(() => setIsLoading(true))
      .then(() => salla.auth.verify(dataToSend, false))
      .then(response => {
        // Track OTP Verified
        Salla.analytics.track('OTP Verified', {
          type: submitData.type,
          method: submitData.type === 'email' ? 'email' : 'mobile'
        });
        verified?.(response)
        return response;
      })
      .then((response) => {
        setIsLoading(false);
        if(inIframe()){
            return;
        }
        //@ts-ignore
        document.querySelector('.s-login-modal-title').innerText=Salla.lang.get('common.elements.welcome')+' '+Salla.config.get('user.first_name')
        //@ts-ignore
        document.querySelector('.s-show').classList.add('s-hidden');
        Salla.event.emit('login:success', {
            response
        });

      })
      .catch((error) => {
        // Track Authentication Failure
        Salla.analytics.track('Authentication Failure', {
          type: submitData.type,
          method: submitData.type === 'email' ? 'email' : 'mobile',
          error: error.response?.data?.error?.message || error.message || 'Unknown error'
        });
        setHasError(true);
        setIsLoading(false);
        otpInputRef.current?.reset();
        setErrorMessage(error.response?.data?.error?.message || otpError);
        console.error(error);
        btnRef.current?.removeAttribute('disabled');
      });
  };

  const formatTimer = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes} : ${formattedSeconds}`;
  };
  const resendTimer = useCallback(() => {
    if (resendMessageRef.current) {

      (resendMessageRef.current.style.display = 'block');
    }
    if (resendRef.current) {
      resendRef.current.style.display = 'none'
    }
    setTimer(30);

    const intervalId = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 0) {
          clearInterval(intervalId);
          if (resendRef.current) {
            resendRef.current.style.display = 'block'
          }
          if (resendMessageRef.current) {
            resendMessageRef.current.style.display = 'none'
          }
        }
        return prevTimer - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Proactive recovery on component mount
  useEffect(() => {
    const storeId = salla.config.get('store.id');
    const recovered = recoverAuthData(storeId, salla.logger);
    
    if (recovered && (recovered.email || recovered.phone)) {
      salla.logger.log('Verify: Auto-recovered data on mount');
      setData(recovered);
      setAutoRecovered(true);
      setDataReady(true);
      resendTimer();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Only auto-submit if data is ready to prevent race conditions
    if (otp.length === 4) {
      if (dataReady) {
        submit();
      } else {
        // Data not ready yet - the effect will re-run when dataReady becomes true
        salla.logger.warn('Verify: OTP entered but data not ready yet, waiting for data...');
      }
    }
    // eslint-disable-next-line
  }, [otp, dataReady]);

  return (
    <div className="s-verify-body" ref={bodyRef}>
      <div className="s-verify-message" dangerouslySetInnerHTML={{ __html: salla.lang.get('pages.profile.verify_message') }} />
      {(data.email || data.phone) && <div dir="ltr" className="s-verify-current" slot="mobile">{data.type === 'mobile' ? `${data.country_key} ${data.phone}` : data.email}</div>}
      <input type="hidden" name="code" maxLength={4} required ref={codeRef} />

      <OTPInput
        autoFocus
        isNumberInput
        length={4}
        ref={otpInputRef}
        className={`s-verify-codes ${hasError ? 'has-error' : ''}`}
        inputClassName={`s-verify-input ${hasError ? 's-has-error' : ''}`}
        onChangeOTP={(otp) => handleChange(otp)}
        onCompleted={(otp) => handleSubmitOTP(otp)}
      />
      <TurnstileWidget
        widgetId="cloudflare-turnstile-resend-widget"
        appearance="interaction-only" />
      {hasError && errorMessage ? <span className="s-verify-error-message">
        {errorMessage}
      </span> : ''}
      <div slot="footer" className="s-verify-footer">
        <button type='button' className="mb-5 btn btn--primary" disabled={isLoading}
          onClick={submit}
          ref={btnRef}>
          {isLoading ? <Loading size="sm" /> : salla.lang.get('pages.profile.verify')}
        </button>
        <p className="s-verify-resend-message" ref={resendMessageRef}>
          {salla.lang.get('blocks.header.resend_after')}
          <b className="s-verify-timer" dir="ltr" ref={timerRef}>{formatTimer(timer)}</b></p>
        <button type='button' className="s-verify-resend" onClick={resendCode} disabled={isLoading} ref={resendRef}>
          {salla.lang.get('blocks.comments.submit')}
        </button>
      </div>
    </div>
  );
});

export default SallaVerify;
